import React, { useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../context/AppContext";
import { sleep } from "../utils/utilFunctions";
import { resetPasswordRequest } from "../api/users";

const ForgotPassword = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email.trim()) {
            return setError(t("forgotPassword.errors.requiredEmail"));
        }

        if (!isValidEmail(email)) {
            return setError(t("forgotPassword.errors.invalidEmail"));
        }

        setIsLoading(true);
        try {
            await resetPasswordRequest(email);
            setSubmitted(true);
        } catch (err) {
            setError(t("forgotPassword.errors.requestFailed")); // Show error if the request failed
        }
        setIsLoading(false);
        setSubmitted(true);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 400,
                    width: "100%",
                    bgcolor: theme.palette.sidebar?.background || theme.palette.background.paper,
                    boxShadow: 6,
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    {!submitted ? (
                        <>
                            <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
                                {t("forgotPassword.title")}
                            </Typography>

                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={t("forgotPassword.email")}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    error={!!error}
                                    helperText={error}
                                    sx={{ mb: 3 }}
                                    InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                                    InputProps={{
                                        style: { color: theme.palette.text.primary },
                                    }}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: 2,
                                    }}
                                >
                                    {t("forgotPassword.submitButton")}
                                </Button>
                            </form>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.primary.main,
                                    textAlign: "center",
                                    mt: 2,
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    "&:hover": {
                                        textDecoration: "underline",
                                    },
                                }}
                                onClick={() => navigate("/login")}
                            >
                                {t("forgotPassword.backToLogin")}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                textAlign="center"
                                color={theme.palette.primary.main}
                                mb={2}
                            >
                                {t("forgotPassword.successTitle")}
                            </Typography>
                            <Typography
                                variant="body1"
                                textAlign="center"
                                mb={3}
                                color={theme.palette.text.primary}
                            >
                                {t("forgotPassword.success")}
                            </Typography>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/login")}
                                sx={{
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    textTransform: "none",
                                }}
                            >
                                {t("forgotPassword.backToLogin")}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ForgotPassword;
